import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Produktmanagement";
const heroImage = "https://net-basis.net/Bilder/ict-product-management-market-adaptation-modular-development.webp";
const heroAlt = "Hände präsentieren modularen Würfel mit roten Bausteinen für ITK-Produktmanagement.";
const heroClaimLine1 = "Perfekt angepasst!";
const heroClaimLine2 = "Sicher platziert!";
const widget2 = "Ihr erfahrener Partner für marktgerechtes Produktmanagement und lokale Produktanpassung in der DACH-Region. Wir unterstützen ausländische Hersteller dabei, technologische Lösungen präzise auf die Anforderungen des deutschsprachigen Marktes abzustimmen.";
const benefitsTitle = "Wie Sie von unserer Expertise im Bereich __Produktmanagement__ profitieren:";
const p1 = "Die Einführung und das Management von Produkten in neuen Märkten stellt Unternehmen vor komplexe Herausforderungen. Unterschiedliche regulatorische Anforderungen, lokale Wettbewerbssituationen und abweichende Kundenbedürfnisse erfordern ein tiefgehendes Verständnis der Zielmärkte. Ohne fundierte lokale Expertise und erfahrene Produktmanagement-Kompetenz vor Ort drohen Fehlinvestitionen, verpasste Marktchancen und ein ineffizienter Einsatz von Ressourcen.";
const p2Text = "NetBasis International setzt genau deshalb hier an und bietet seinen Herstellerpartnern umfassendes Know-how: Wir übernehmen das gesamte Produktmanagement im DACH-Raum – von der Marktanalyse über die Produktanpassung bis hin zur kontinuierlichen Betreuung und Optimierung Ihres Produktportfolios.";
const p2Parts = p2Text.split(COMPANY_NAME);
const p3 = "Von Düsseldorf aus stellen wir sicher, dass Ihre Produkte optimal auf die Anforderungen vor Ort abgestimmt sind. Wir koordinieren sämtliche produktrelevanten Prozesse, überwachen die Einhaltung gesetzlicher Vorschriften und sorgen für eine effiziente Kommunikation zwischen Ihrem Stammhaus und den Akteuren in der Handelsregion. So sind Sie vom ersten Tag an wettbewerbsfähig positioniert.";
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Professionelles Produktmanagement ohne Aufbau eigener Strukturen vor Ort",
  "Marktgerechte Produktanpassung an lokale Anforderungen und Standards",
  "Kontinuierliche Marktbeobachtung und Wettbewerbsanalyse",
  "Effizientes Lifecycle-Management für Ihr gesamtes Produktportfolio",
  "Optimierung von Lieferketten und Vertriebsprozessen",
  "Sicherstellung der regulatorischen Konformität im DACH-Raum",
  "Entlastung Ihres Stammhauses von operativen Produktmanagement-Aufgaben",
  "Zugang zu lokalen Netzwerken, Dienstleistern und Vertriebskanälen",
  "Schnelle Reaktionsfähigkeit auf Marktveränderungen und Kundenfeedback",
  "Skalierbare Lösung – flexibel an Ihre Wachstumsziele anpassbar",
];

export default function ProduktmanagementDePage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Produktmanagement | NetBasis International"
        description="Zielgerichtete Anpassung und Positionierung Ihrer Produkte für die spezifischen Anforderungen der DACH-Zielmärkte."
        ogUrl="https://net-basis.net/de/services/business-development/produktmanagement"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-center overflow-hidden pointer-events-none pt-12 sm:pt-16 md:pt-24 lg:pt-20">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-center px-4 leading-[1.15] md:leading-[1.5] pm-hero-text-slide"
                  style={{ fontFamily: '"Open Sans", sans-serif' }}
                >
                  <span className="block sm:inline">{heroClaimLine1}</span>
                  <span className="hidden sm:inline"> </span>
                  <span className="block sm:inline">{heroClaimLine2}</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="text-foreground-950 font-semibold text-sm md:text-base mb-4 leading-relaxed">
              {widget2}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {(() => {
                  const parts = p1.split(COMPANY_NAME);
                  return parts.map((part, i) => (
                    <span key={i}>
                      {part}
                      {i < parts.length - 1 && (
                        <strong className="text-primary-600">{COMPANY_NAME}</strong>
                      )}
                    </span>
                  ));
                })()}
              </p>
              <p>
                {p2Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p2Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>{p3}</p>
            </div>

            {/* Green heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>

            {/* Benefits with green checkmarks */}
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}